import {VoidFunctionComponent, useEffect, useRef} from 'react';
import {useGoogleMap} from '@ubilabs/google-maps-react-hooks';
import MarkerClusterer from '@google/markerclustererplus';
import {Cluster} from '@google/markerclustererplus/dist/cluster';

import {clusterMarkerStyles} from '../../../../constants/cluster-markers';

import getClusterIconInfo from '../../../libs/get-cluster-icon-info';

interface Props {
  markers: Array<google.maps.Marker>;
}

const MapMarkerClusterer: VoidFunctionComponent<Props> = props => {
  const {markers} = props;
  const {map} = useGoogleMap();
  const markerClusterer = useRef<MarkerClusterer>();

  // Create and update marker clusterer
  useEffect(() => {
    if (map) {
      const options = {
        gridSize: 20,
        zoomOnClick: false,
        calculator: getClusterIconInfo,
        styles: clusterMarkerStyles
      };

      // Create marker clusterer
      markerClusterer.current = new MarkerClusterer(map, markers, options);
      markerClusterer.current.setIgnoreHidden(true);

      // Zoom into the cluster when clicking on the cluster marker
      google.maps.event.addListener(
        markerClusterer.current,
        'clusterclick',
        (cluster: Cluster) => {
          const clusterBounds = cluster.getBounds();
          map.fitBounds(clusterBounds);
        }
      );
    }

    // Clean up marker clusterer
    return () => {
      if (markerClusterer.current) {
        markerClusterer.current.clearMarkers();
        google.maps.event.clearInstanceListeners(markerClusterer.current);
      }
    };
  }, [map]);

  // Update all clusters and markers on the map
  useEffect(() => {
    if (markerClusterer.current) {
      markerClusterer.current.clearMarkers();
      markerClusterer.current.addMarkers(markers);
    }
  }, [
    JSON.stringify(
      markers.map(marker => ({
        id: marker.get('id'),
        year: marker.get('year')
      }))
    )
  ]);

  // Update cluster marker visibility
  useEffect(() => {
    const updateHandler = () => {
      if (markerClusterer.current) {
        markerClusterer.current.repaint();
      }
    };

    document.addEventListener('map_markers_visibility_changed', updateHandler);

    return () => {
      document.removeEventListener('map_markers_visibility_changed', updateHandler);
    };
  }, []);

  return null;
};

export default MapMarkerClusterer;
